import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../../api/api';
import QuranService from '../../services/quranService';
import '../../styles/CreateAssignmentPage.css';

const CreateLearningPlanPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get any passed params from location state
  const initialStudentId = location.state?.studentId || null;
  const initialStudentName = location.state?.studentName || null;
  const initialHalaqaId = location.state?.halaqaId || null; // Class context from Student Profile
  const restoredFormState = location.state?.formData?.formState || null; // Restored form state from preview page

  // State for general UI
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [hasRestoredForm, setHasRestoredForm] = useState(false); // Track if form has been restored from preview

  // State for dropdowns
  const [showClassDropdown, setShowClassDropdown] = useState(false);
  const [showStudentsDropdown, setShowStudentsDropdown] = useState(false);
  const [showStartSurahDropdown, setShowStartSurahDropdown] = useState(false);
  const [showEndSurahDropdown, setShowEndSurahDropdown] = useState(false);
  const [showLinesSurahDropdown, setShowLinesSurahDropdown] = useState(false);
  const [startSurahSearchText, setStartSurahSearchText] = useState('');
  const [endSurahSearchText, setEndSurahSearchText] = useState('');
  const [linesSurahSearchText, setLinesSurahSearchText] = useState('');
  const [surahSearchText, setSurahSearchText] = useState('');

  // Plan data - CLASS FIRST, then student
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [goalType, setGoalType] = useState('lines');
  const [assignmentType, setAssignmentType] = useState('new-memorization');

  // Goal-specific fields
  const [pageFrom, setPageFrom] = useState('');
  const [pageTo, setPageTo] = useState('');
  const [verseStartSurah, setVerseStartSurah] = useState(null);
  const [verseStartVerse, setVerseStartVerse] = useState('1');
  const [verseEndSurah, setVerseEndSurah] = useState(null);
  const [verseEndVerse, setVerseEndVerse] = useState('');
  const [juzNumber, setJuzNumber] = useState('');
  const [selectedSurahs, setSelectedSurahs] = useState([]);
  const [dailyLines, setDailyLines] = useState('4');
  const [linesStartSurah, setLinesStartSurah] = useState(null);
  const [linesStartVerse, setLinesStartVerse] = useState('1');
  const [linesDirection, setLinesDirection] = useState('backward'); // Direction for lines goal

  // Timeline fields
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1); // Default 1 month
    return date.toISOString().split('T')[0];
  });

  // Schedule fields
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [specificDays, setSpecificDays] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);

  // Notes
  const [notes, setNotes] = useState('');

  // Surah options
  const [surahOptions, setSurahOptions] = useState([]);

  // Days of week for selection
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Load teacher's classes and surahs on init
  useEffect(() => {
    // Load surahs
    const surahs = QuranService.getAllSurahs();
    setSurahOptions(surahs);

    // Fetch teacher's classes (same as Assignment creation)
    const fetchClasses = async () => {
      try {
        setLoadingClasses(true);
        const response = await api.get('/halaqas');
        const classesData = response.data.data || response.data || [];
        const classesArray = Array.isArray(classesData) ? classesData : [];
        setAvailableClasses(classesArray);

        // Auto-selection logic (only if NOT restoring from preview)
        if (!restoredFormState) {
          if (initialHalaqaId) {
            // If initialHalaqaId was passed (from Student Profile), auto-select that class
            const matchingClass = classesArray.find(c => c._id === initialHalaqaId);
            if (matchingClass) {
              setSelectedClass(matchingClass);
            } else {
              // Student's class not in teacher's classes - show error
              setFormErrors(prev => ({
                ...prev,
                class: 'Student is not in any of your classes'
              }));
            }
          } else if (classesArray.length === 1) {
            // Auto-select if teacher has only one class
            setSelectedClass(classesArray[0]);
          } else if (classesArray.length > 1) {
            // For teachers with multiple classes, try to pre-select the most recent class
            const lastSelectedClassId = localStorage.getItem('lastSelectedClassId');
            if (lastSelectedClassId) {
              const lastClass = classesArray.find(c => c._id === lastSelectedClassId);
              if (lastClass) {
                setSelectedClass(lastClass);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        setAvailableClasses([]);
        setFormErrors(prev => ({
          ...prev,
          class: 'Failed to load classes. Please refresh the page.'
        }));
      } finally {
        setLoadingClasses(false);
      }
    };

    fetchClasses();
  }, [initialHalaqaId]);

  // Restore form state when navigating back from preview page
  useEffect(() => {
    if (restoredFormState && availableClasses.length > 0 && !hasRestoredForm) {
      // Restore all form fields
      if (restoredFormState.selectedClass) {
        const restoredClass = availableClasses.find(c => c._id === restoredFormState.selectedClass._id);
        if (restoredClass) {
          setSelectedClass(restoredClass);
        }
      }
      // Note: selectedStudent will be restored after students are loaded in the next useEffect
      if (restoredFormState.goalType) setGoalType(restoredFormState.goalType);
      if (restoredFormState.assignmentType) setAssignmentType(restoredFormState.assignmentType);
      if (restoredFormState.pageFrom) setPageFrom(restoredFormState.pageFrom);
      if (restoredFormState.pageTo) setPageTo(restoredFormState.pageTo);
      if (restoredFormState.verseStartSurah) setVerseStartSurah(restoredFormState.verseStartSurah);
      if (restoredFormState.verseStartVerse) setVerseStartVerse(restoredFormState.verseStartVerse);
      if (restoredFormState.verseEndSurah) setVerseEndSurah(restoredFormState.verseEndSurah);
      if (restoredFormState.verseEndVerse) setVerseEndVerse(restoredFormState.verseEndVerse);
      if (restoredFormState.juzNumber) setJuzNumber(restoredFormState.juzNumber);
      if (restoredFormState.selectedSurahs) setSelectedSurahs(restoredFormState.selectedSurahs);
      if (restoredFormState.dailyLines) setDailyLines(restoredFormState.dailyLines);
      if (restoredFormState.linesStartSurah) setLinesStartSurah(restoredFormState.linesStartSurah);
      if (restoredFormState.linesStartVerse) setLinesStartVerse(restoredFormState.linesStartVerse);
      if (restoredFormState.linesDirection) setLinesDirection(restoredFormState.linesDirection);
      if (restoredFormState.startDate) setStartDate(restoredFormState.startDate);
      if (restoredFormState.endDate) setEndDate(restoredFormState.endDate);
      if (restoredFormState.daysPerWeek) setDaysPerWeek(restoredFormState.daysPerWeek);
      if (restoredFormState.specificDays) setSpecificDays(restoredFormState.specificDays);
      if (restoredFormState.notes) setNotes(restoredFormState.notes);

      // Mark restoration as complete
      setHasRestoredForm(true);
    }
  }, [restoredFormState, availableClasses, hasRestoredForm]);

  // Fetch students when class is selected (same as Assignment creation)
  useEffect(() => {
    // Only clear selected student if we're not in the middle of restoration
    // Once restoration is complete, normal behavior resumes
    if (!restoredFormState && selectedClass) {
      setSelectedStudent(null);
    }

    const abortController = new AbortController();

    const fetchStudentsForClass = async () => {
      if (!selectedClass) {
        setStudents([]);
        setSelectedStudent(null);
        return;
      }

      // Save selected class to localStorage for future auto-selection
      localStorage.setItem('lastSelectedClassId', selectedClass._id);

      try {
        setLoadingStudents(true);
        setFormErrors(prev => ({ ...prev, student: null })); // Clear student errors

        const response = await api.get(`/halaqas/${selectedClass._id}/students`, {
          signal: abortController.signal
        });

        const studentsData = response.data.data || response.data || [];
        const studentsArray = Array.isArray(studentsData) ? studentsData : [];
        setStudents(studentsArray);

        // Priority 1: Restore student from preview page
        if (restoredFormState?.selectedStudent) {
          const restoredStudent = studentsArray.find(s => s._id === restoredFormState.selectedStudent._id);
          if (restoredStudent) {
            setSelectedStudent(restoredStudent);
          }
        }
        // Priority 2: If initialStudentId was passed, auto-select if they're in this class
        else if (initialStudentId && initialStudentName) {
          const student = studentsArray.find(s => s._id === initialStudentId);
          if (student) {
            setSelectedStudent(student);
          }
        }
      } catch (error) {
        // Don't show error if request was aborted (user changed class quickly)
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          return;
        }

        console.error('Error fetching students for class:', error);
        setStudents([]);
        setFormErrors(prev => ({
          ...prev,
          student: 'Failed to load students. Please try again.'
        }));
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudentsForClass();

    // Cleanup: abort ongoing request if class changes
    return () => {
      abortController.abort();
    };
  }, [selectedClass, initialStudentId, initialStudentName, restoredFormState]);

  // Handle day selection
  const toggleDay = (day) => {
    if (specificDays.includes(day)) {
      const newDays = specificDays.filter(d => d !== day);
      setSpecificDays(newDays);
      setDaysPerWeek(newDays.length);
    } else {
      const newDays = [...specificDays, day];
      setSpecificDays(newDays);
      setDaysPerWeek(newDays.length);
    }
  };

  // Handle surah selection for surahs goal
  const toggleSurahSelection = (surah) => {
    const isSelected = selectedSurahs.some(s => s.id === surah.id);
    if (isSelected) {
      setSelectedSurahs(selectedSurahs.filter(s => s.id !== surah.id));
    } else {
      const newSurahs = [...selectedSurahs, { id: surah.id, name: surah.name }];
      // Sort by surah ID to maintain Quranic order
      newSurahs.sort((a, b) => parseInt(a.id) - parseInt(b.id));
      setSelectedSurahs(newSurahs);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!selectedClass) {
      errors.class = 'Please select a class';
    }

    if (!selectedStudent) {
      errors.student = 'Please select a student';
    }

    if (!startDate || !endDate) {
      errors.timeline = 'Please select start and end dates';
    } else {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

      if (daysDiff <= 0) {
        errors.timeline = 'End date must be after start date';
      } else if (daysDiff < 7) {
        errors.timeline = 'Timeline must be at least 7 days';
      } else if (daysDiff > 730) {
        errors.timelineWarning = 'Timeline exceeds 2 years. Consider a shorter duration for better tracking.';
      }
    }

    if (specificDays.length === 0) {
      errors.schedule = 'Please select at least one day';
    }

    // Goal-specific validation
    if (goalType === 'pages') {
      if (!pageFrom || !pageTo) {
        errors.goal = 'Please enter page range (from and to)';
      } else if (parseInt(pageFrom) < 1 || parseInt(pageFrom) > 604 || parseInt(pageTo) < 1 || parseInt(pageTo) > 604) {
        errors.goal = 'Page numbers must be between 1 and 604';
      }
    } else if (goalType === 'verses') {
      if (!verseStartSurah || !verseEndSurah) {
        errors.goal = 'Please select both starting and ending surahs';
      } else if (!verseStartVerse || !verseEndVerse) {
        errors.goal = 'Please enter both starting and ending verses';
      } else {
        const startSurahId = parseInt(verseStartSurah.id);
        const endSurahId = parseInt(verseEndSurah.id);
        const startVerse = parseInt(verseStartVerse);
        const endVerse = parseInt(verseEndVerse);

        // Validate starting verse is within surah range
        const maxStartVerse = verseStartSurah.verseCount || 286;
        if (startVerse < 1 || startVerse > maxStartVerse) {
          errors.goal = `Starting verse must be between 1 and ${maxStartVerse} for ${verseStartSurah.name}`;
        }

        // Validate ending verse is within surah range
        const maxEndVerse = verseEndSurah.verseCount || 286;
        if (endVerse < 1 || endVerse > maxEndVerse) {
          errors.goal = `Ending verse must be between 1 and ${maxEndVerse} for ${verseEndSurah.name}`;
        }

        // Validate start and end are not the same
        // Note: Reverse order (backward direction) is now supported by backend
        if (startSurahId === endSurahId && startVerse === endVerse) {
          errors.goal = 'Starting and ending verses cannot be the same';
        }
      }
    } else if (goalType === 'juz') {
      if (!juzNumber || parseInt(juzNumber) < 1 || parseInt(juzNumber) > 30) {
        errors.goal = 'Juz number must be between 1 and 30';
      }
    } else if (goalType === 'surahs') {
      if (selectedSurahs.length === 0) {
        errors.goal = 'Please select at least one surah';
      }
    } else if (goalType === 'lines') {
      if (!dailyLines || parseInt(dailyLines) < 1 || parseInt(dailyLines) > 15) {
        errors.goal = 'Daily lines must be between 1 and 15';
      } else if (!linesStartSurah) {
        errors.goal = 'Please select a starting surah';
      } else if (!linesStartVerse || parseInt(linesStartVerse) < 1 || parseInt(linesStartVerse) > (linesStartSurah?.verseCount || 999)) {
        errors.goal = `Starting verse must be between 1 and ${linesStartSurah?.verseCount || 999} for ${linesStartSurah?.name || 'selected surah'}`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check for existing active plans
  const checkExistingPlans = async () => {
    try {
      const response = await api.get(`/learning-plans/teacher`);
      const allPlans = response.data.data || [];

      const conflictingPlan = allPlans.find(p =>
        p.studentId._id === selectedStudent._id &&
        p.status === 'active' &&
        p.assignmentType === assignmentType
      );

      if (conflictingPlan) {
        setFormErrors({
          student: `Student already has an active ${assignmentType.replace('-', ' ')} learning plan. Please pause or complete it first.`
        });
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking existing plans:', error);
      // Allow creation if check fails
      return true;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check for duplicate active plans
    const canCreate = await checkExistingPlans();
    if (!canCreate) {
      return;
    }

    setLoading(true);

    try {
      // Build goal details based on type
      let goalDetails = {};

      if (goalType === 'pages') {
        goalDetails = {
          from: parseInt(pageFrom),
          to: parseInt(pageTo)
        };
      } else if (goalType === 'verses') {
        goalDetails = {
          startFrom: {
            surah: parseInt(verseStartSurah.id),
            verse: parseInt(verseStartVerse)
          },
          endAt: {
            surah: parseInt(verseEndSurah.id),
            verse: parseInt(verseEndVerse)
          }
        };
      } else if (goalType === 'juz') {
        goalDetails = {
          number: parseInt(juzNumber)
        };
      } else if (goalType === 'surahs') {
        goalDetails = {
          surahIds: selectedSurahs.map(s => parseInt(s.id))
        };
      } else if (goalType === 'lines') {
        goalDetails = {
          dailyLines: parseInt(dailyLines),
          startFrom: {
            surah: parseInt(linesStartSurah.id),
            verse: parseInt(linesStartVerse)
          },
          direction: linesDirection // Include direction for lines goal
        };
      }

      const planData = {
        studentId: selectedStudent._id,
        halaqaId: selectedClass._id,
        goalType,
        goalDetails,
        startDate,
        endDate,
        schedule: {
          daysPerWeek,
          specificDays
        },
        assignmentType,
        notes
      };

      const response = await api.post('/learning-plans', planData);

      if (response.data.success) {
        // Navigate to preview page with planData (not saved yet) and suggestions
        // Also save form state for back navigation
        navigate('/learning-plans/preview', {
          state: {
            planData: response.data.data.planData, // Unsaved plan data
            suggestedAssignments: response.data.data.suggestedAssignments,
            warnings: response.data.data.warnings || [],
            studentName: selectedStudent.name,
            // Save form state for back navigation
            formState: {
              selectedClass,
              selectedStudent,
              goalType,
              assignmentType,
              pageFrom,
              pageTo,
              verseStartSurah,
              verseStartVerse,
              verseEndSurah,
              verseEndVerse,
              juzNumber,
              selectedSurahs,
              dailyLines,
              linesStartSurah,
              linesStartVerse,
              linesDirection,
              startDate,
              endDate,
              daysPerWeek,
              specificDays,
              notes
            }
          }
        });
      }
    } catch (error) {
      console.error('Error creating learning plan:', error);
      setFormErrors({ submit: error.response?.data?.message || 'Failed to create learning plan. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Filtered surahs for dropdowns
  const filteredStartSurahs = surahOptions.filter(surah =>
    surah.name.toLowerCase().includes(startSurahSearchText.toLowerCase()) ||
    surah.nameArabic.includes(startSurahSearchText) ||
    surah.id.toString().includes(startSurahSearchText)
  );

  const filteredEndSurahs = surahOptions.filter(surah =>
    surah.name.toLowerCase().includes(endSurahSearchText.toLowerCase()) ||
    surah.nameArabic.includes(endSurahSearchText) ||
    surah.id.toString().includes(endSurahSearchText)
  );

  const filteredSurahs = surahOptions.filter(surah =>
    surah.name.toLowerCase().includes(surahSearchText.toLowerCase()) ||
    surah.nameArabic.includes(surahSearchText) ||
    surah.id.toString().includes(surahSearchText)
  );

  return (
    <div className="create-assignment-container">
      <div className="header">
        <h1>Create Learning Plan</h1>
        <Link to="/learning-plans" className="back-btn">
          ← Back to Plans
        </Link>
      </div>

      {/* Beta Feature Notice */}
      <div style={{
        marginBottom: '20px',
        padding: '12px 16px',
        backgroundColor: '#e3f2fd',
        border: '1px solid #2196f3',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#1565c0'
      }}>
        <strong>🧪 Beta Feature:</strong> Learning Plans is currently in beta. Your feedback helps us improve this feature!
      </div>

      {formErrors.submit && (
        <div className="error-message" style={{marginBottom: '20px', padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '8px'}}>
          {formErrors.submit}
        </div>
      )}

      {formErrors.timelineWarning && (
        <div className="warning-message" style={{marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '8px', border: '1px solid #ffc107'}}>
          ⚠️ {formErrors.timelineWarning}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-card">
          {/* Class Selection - SELECT CLASS FIRST */}
          <div className="form-section">
            <h3>Class (Halaqa)</h3>
            <div className="form-group">
              <label>Select Class *</label>
              {loadingClasses ? (
                <div style={{padding: '10px', color: '#666'}}>Loading classes...</div>
              ) : availableClasses.length === 0 ? (
                <div style={{padding: '10px', color: '#666', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107'}}>
                  No classes assigned to you
                </div>
              ) : (
                <div className="custom-select">
                  <div
                    className="select-display"
                    onClick={() => setShowClassDropdown(!showClassDropdown)}
                  >
                    <span>{selectedClass ? selectedClass.name : 'Select a class...'}</span>
                    <i className="dropdown-icon">▼</i>
                  </div>

                  {showClassDropdown && (
                    <div className="select-dropdown">
                      {availableClasses.map(cls => (
                        <div
                          key={cls._id}
                          className={`dropdown-item ${selectedClass?._id === cls._id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedClass(cls);
                            setShowClassDropdown(false);
                          }}
                        >
                          {cls.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {formErrors.class && <span className="error-text">{formErrors.class}</span>}
            </div>
          </div>

          {/* Student Selection - appears after class is selected */}
          {selectedClass && (
            <div className="form-section">
              <h3>Student</h3>
              <div className="form-group">
                <label>Select Student *</label>
                {loadingStudents ? (
                  <div style={{padding: '10px', color: '#666'}}>Loading students...</div>
                ) : students.length === 0 ? (
                  <div style={{padding: '10px', color: '#666', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107'}}>
                    No students found in this class
                  </div>
                ) : (
                  <div className="custom-select">
                    <div
                      className="select-display"
                      onClick={() => setShowStudentsDropdown(!showStudentsDropdown)}
                    >
                      <span>{selectedStudent ? selectedStudent.name : 'Select a student...'}</span>
                      <i className="dropdown-icon">▼</i>
                    </div>

                    {showStudentsDropdown && (
                      <div className="select-dropdown">
                        {students.map(student => (
                          <div
                            key={student._id}
                            className={`dropdown-item ${selectedStudent && selectedStudent._id === student._id ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedStudent(student);
                              setShowStudentsDropdown(false);
                            }}
                          >
                            {student.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {formErrors.student && <span className="error-text">{formErrors.student}</span>}
              </div>
            </div>
          )}

          {/* Goal Type Selection */}
          <div className="form-section">
            <h3>Goal Type</h3>
            <div className="form-group">
              <label>What would you like the student to complete? *</label>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '10px'}}>
                Choose how you want to measure this learning plan
              </p>
              <div className="button-group" style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                <button
                  type="button"
                  className={`option-btn ${goalType === 'verses' ? 'active' : ''}`}
                  onClick={() => setGoalType('verses')}
                >
                  Verses
                </button>
                <button
                  type="button"
                  className={`option-btn ${goalType === 'lines' ? 'active' : ''}`}
                  onClick={() => setGoalType('lines')}
                >
                  Lines
                </button>

                {/* Hidden for Beta Phase 1 - Coming Soon */}
                <button
                  type="button"
                  className="option-btn"
                  disabled
                  style={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    position: 'relative'
                  }}
                  title="Coming soon based on teacher feedback"
                >
                  Pages
                  <span style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: 'bold'
                  }}>Soon</span>
                </button>
                <button
                  type="button"
                  className="option-btn"
                  disabled
                  style={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    position: 'relative'
                  }}
                  title="Coming soon based on teacher feedback"
                >
                  Juz
                  <span style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: 'bold'
                  }}>Soon</span>
                </button>
                <button
                  type="button"
                  className="option-btn"
                  disabled
                  style={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    position: 'relative'
                  }}
                  title="Coming soon based on teacher feedback"
                >
                  Surahs
                  <span style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: 'bold'
                  }}>Soon</span>
                </button>
              </div>
            </div>

            {/* Assignment Type Selection */}
            <div className="form-group">
              <label>Assignment Type *</label>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '10px'}}>
                How should students practice this content?
              </p>
              <div className="button-group" style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                <button
                  type="button"
                  className={`option-btn ${assignmentType === 'new-memorization' ? 'active' : ''}`}
                  onClick={() => setAssignmentType('new-memorization')}
                  style={{
                    backgroundColor: assignmentType === 'new-memorization' ? '#4a90e2' : '#fff',
                    color: assignmentType === 'new-memorization' ? '#fff' : '#333',
                    border: assignmentType === 'new-memorization' ? '2px solid #4a90e2' : '2px solid #ddd',
                    fontWeight: assignmentType === 'new-memorization' ? '600' : '400'
                  }}
                >
                  {assignmentType === 'new-memorization' && '✓ '}New Memorization
                </button>
                <button
                  type="button"
                  className={`option-btn ${assignmentType === 'revision' ? 'active' : ''}`}
                  onClick={() => setAssignmentType('revision')}
                  style={{
                    backgroundColor: assignmentType === 'revision' ? '#4a90e2' : '#fff',
                    color: assignmentType === 'revision' ? '#fff' : '#333',
                    border: assignmentType === 'revision' ? '2px solid #4a90e2' : '2px solid #ddd',
                    fontWeight: assignmentType === 'revision' ? '600' : '400'
                  }}
                >
                  {assignmentType === 'revision' && '✓ '}Revision
                </button>
                <button
                  type="button"
                  className={`option-btn ${assignmentType === 'recitation' ? 'active' : ''}`}
                  onClick={() => setAssignmentType('recitation')}
                  style={{
                    backgroundColor: assignmentType === 'recitation' ? '#4a90e2' : '#fff',
                    color: assignmentType === 'recitation' ? '#fff' : '#333',
                    border: assignmentType === 'recitation' ? '2px solid #4a90e2' : '2px solid #ddd',
                    fontWeight: assignmentType === 'recitation' ? '600' : '400'
                  }}
                >
                  {assignmentType === 'recitation' && '✓ '}Recitation
                </button>
              </div>
              <small style={{color: '#666', display: 'block', marginTop: '5px'}}>
                All assignments in this plan will use this type
              </small>
            </div>

            {/* Goal-specific inputs */}
            {goalType === 'pages' && (
              <div className="form-group">
                <label>Page Range *</label>
                <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                  <input
                    type="number"
                    placeholder="From (1-604)"
                    value={pageFrom}
                    onChange={(e) => setPageFrom(e.target.value)}
                    min="1"
                    max="604"
                    style={{flex: 1}}
                  />
                  <span>to</span>
                  <input
                    type="number"
                    placeholder="To (1-604)"
                    value={pageTo}
                    onChange={(e) => setPageTo(e.target.value)}
                    min="1"
                    max="604"
                    style={{flex: 1}}
                  />
                </div>
                <small style={{color: '#666', display: 'block', marginTop: '5px'}}>
                  Enter page numbers between 1 and 604 (e.g., 582 to 604 for Juz 30)
                </small>
              </div>
            )}

            {goalType === 'verses' && (
              <div className="form-group">
                <label>Verse Range *</label>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                  {/* From Section */}
                  <div>
                    <label style={{fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '10px', display: 'block'}}>From</label>

                    <div style={{marginBottom: '10px'}}>
                      <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Surah</label>
                      <div className="custom-select">
                        <div
                          className="select-display"
                          onClick={() => setShowStartSurahDropdown(!showStartSurahDropdown)}
                        >
                          <span>{verseStartSurah ? `${verseStartSurah.id}. ${verseStartSurah.name}` : 'Select surah...'}</span>
                          <i className="dropdown-icon">▼</i>
                        </div>

                        {showStartSurahDropdown && (
                          <div className="select-dropdown">
                            <input
                              type="text"
                              placeholder="Search surah..."
                              value={startSurahSearchText}
                              onChange={(e) => setStartSurahSearchText(e.target.value)}
                              className="search-input"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                              {filteredStartSurahs.map(surah => (
                                <div
                                  key={surah.id}
                                  className={`dropdown-item ${verseStartSurah && verseStartSurah.id === surah.id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setVerseStartSurah(surah);
                                    setShowStartSurahDropdown(false);
                                    setStartSurahSearchText('');
                                  }}
                                >
                                  {surah.id}. {surah.name} - {surah.nameArabic}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Verse</label>
                      <input
                        type="number"
                        placeholder="Verse #"
                        value={verseStartVerse}
                        onChange={(e) => setVerseStartVerse(e.target.value)}
                        min="1"
                        max={verseStartSurah ? verseStartSurah.verseCount : 999}
                      />
                      {verseStartSurah && (
                        <small style={{color: '#666', display: 'block', marginTop: '3px', fontSize: '12px'}}>
                          Max: {verseStartSurah.verseCount}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* To Section */}
                  <div>
                    <label style={{fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '10px', display: 'block'}}>To</label>

                    <div style={{marginBottom: '10px'}}>
                      <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Surah</label>
                      <div className="custom-select">
                        <div
                          className="select-display"
                          onClick={() => setShowEndSurahDropdown(!showEndSurahDropdown)}
                        >
                          <span>{verseEndSurah ? `${verseEndSurah.id}. ${verseEndSurah.name}` : 'Select surah...'}</span>
                          <i className="dropdown-icon">▼</i>
                        </div>

                        {showEndSurahDropdown && (
                          <div className="select-dropdown">
                            <input
                              type="text"
                              placeholder="Search surah..."
                              value={endSurahSearchText}
                              onChange={(e) => setEndSurahSearchText(e.target.value)}
                              className="search-input"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                              {filteredEndSurahs.map(surah => (
                                <div
                                  key={surah.id}
                                  className={`dropdown-item ${verseEndSurah && verseEndSurah.id === surah.id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setVerseEndSurah(surah);
                                    setShowEndSurahDropdown(false);
                                    setEndSurahSearchText('');
                                  }}
                                >
                                  {surah.id}. {surah.name} - {surah.nameArabic}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Verse</label>
                      <input
                        type="number"
                        placeholder="Verse #"
                        value={verseEndVerse}
                        onChange={(e) => setVerseEndVerse(e.target.value)}
                        min="1"
                        max={verseEndSurah ? verseEndSurah.verseCount : 999}
                      />
                      {verseEndSurah && (
                        <small style={{color: '#666', display: 'block', marginTop: '3px', fontSize: '12px'}}>
                          Max: {verseEndSurah.verseCount}
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                {/* Range Preview */}
                {verseStartSurah && verseEndSurah && verseStartVerse && verseEndVerse && (
                  <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#f5f7fa', borderRadius: '8px'}}>
                    <strong style={{color: '#333'}}>Range: </strong>
                    <span style={{color: '#666'}}>
                      {verseStartSurah.name} ({verseStartVerse}) to {verseEndSurah.name} ({verseEndVerse})
                    </span>
                  </div>
                )}
              </div>
            )}

            {goalType === 'juz' && (
              <div className="form-group">
                <label>Juz Number *</label>
                <input
                  type="number"
                  placeholder="Enter Juz number (1-30)"
                  value={juzNumber}
                  onChange={(e) => setJuzNumber(e.target.value)}
                  min="1"
                  max="30"
                />
                <small style={{color: '#666', display: 'block', marginTop: '5px'}}>
                  Enter a Juz number between 1 and 30
                </small>
              </div>
            )}

            {goalType === 'surahs' && (
              <div className="form-group">
                <label>Select Surahs *</label>
                <div className="custom-select">
                  <input
                    type="text"
                    placeholder="Search surahs..."
                    value={surahSearchText}
                    onChange={(e) => setSurahSearchText(e.target.value)}
                    className="search-input"
                  />
                  <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '8px', marginTop: '10px'}}>
                    {filteredSurahs.map(surah => {
                      const isSelected = selectedSurahs.some(s => s.id === surah.id);
                      return (
                        <div
                          key={surah.id}
                          className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleSurahSelection(surah)}
                          style={{cursor: 'pointer'}}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            style={{marginRight: '10px'}}
                          />
                          {surah.id}. {surah.name} - {surah.nameArabic}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {selectedSurahs.length > 0 && (
                  <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#f5f7fa', borderRadius: '8px'}}>
                    <strong>Selected ({selectedSurahs.length}):</strong>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px'}}>
                      {selectedSurahs.map(surah => (
                        <span
                          key={surah.id}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#4a90e2',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        >
                          {surah.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {goalType === 'lines' && (
              <div className="form-group">
                <label>Lines-Based Configuration *</label>

                {/* Daily Lines Target */}
                <div style={{marginBottom: '15px'}}>
                  <label style={{fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '5px', display: 'block'}}>
                    Daily Lines Target *
                  </label>
                  <input
                    type="number"
                    placeholder="Lines per day (1-15)"
                    value={dailyLines}
                    onChange={(e) => setDailyLines(e.target.value)}
                    min="1"
                    max="15"
                    style={{width: '100%'}}
                  />
                  <small style={{color: '#666', display: 'block', marginTop: '5px'}}>
                    Target lines per assignment (approximate). Actual lines may vary slightly to complete full verses. Each Mushaf page has 15 lines.
                  </small>
                </div>

                {/* Direction Selector */}
                <div style={{marginBottom: '15px'}}>
                  <label style={{fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block'}}>
                    Memorization Direction *
                  </label>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button
                      type="button"
                      className={`option-btn ${linesDirection === 'forward' ? 'active' : ''}`}
                      onClick={() => setLinesDirection('forward')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: linesDirection === 'forward' ? '#4a90e2' : '#fff',
                        color: linesDirection === 'forward' ? '#fff' : '#333',
                        border: linesDirection === 'forward' ? '2px solid #4a90e2' : '2px solid #ddd',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: linesDirection === 'forward' ? '600' : '400',
                        transition: 'all 0.2s'
                      }}
                    >
                      ↓ Forward (Al-Fatihah → An-Nas)
                    </button>
                    <button
                      type="button"
                      className={`option-btn ${linesDirection === 'backward' ? 'active' : ''}`}
                      onClick={() => setLinesDirection('backward')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: linesDirection === 'backward' ? '#4a90e2' : '#fff',
                        color: linesDirection === 'backward' ? '#fff' : '#333',
                        border: linesDirection === 'backward' ? '2px solid #4a90e2' : '2px solid #ddd',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: linesDirection === 'backward' ? '600' : '400',
                        transition: 'all 0.2s'
                      }}
                    >
                      ↑ Backward (An-Nas → Al-Fatihah)
                    </button>
                  </div>
                  <small style={{color: '#666', display: 'block', marginTop: '5px'}}>
                    Choose the order in which assignments will be generated from your starting point.
                  </small>
                </div>

                {/* Starting Point */}
                <div>
                  <label style={{fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '10px', display: 'block'}}>
                    Starting Point *
                  </label>

                  <div style={{marginBottom: '10px'}}>
                    <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Surah</label>
                    <div className="custom-select">
                      <div
                        className="select-display"
                        onClick={() => setShowLinesSurahDropdown(!showLinesSurahDropdown)}
                      >
                        <span>{linesStartSurah ? `${linesStartSurah.id}. ${linesStartSurah.name}` : 'Select surah...'}</span>
                        <i className="dropdown-icon">▼</i>
                      </div>

                      {showLinesSurahDropdown && (
                        <div className="select-dropdown">
                          <input
                            type="text"
                            placeholder="Search surah..."
                            value={linesSurahSearchText}
                            onChange={(e) => setLinesSurahSearchText(e.target.value)}
                            className="search-input"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                            {surahOptions.filter(surah =>
                              surah.name.toLowerCase().includes(linesSurahSearchText.toLowerCase()) ||
                              surah.nameArabic.includes(linesSurahSearchText) ||
                              surah.id.toString().includes(linesSurahSearchText)
                            ).map(surah => (
                              <div
                                key={surah.id}
                                className={`dropdown-item ${linesStartSurah && linesStartSurah.id === surah.id ? 'selected' : ''}`}
                                onClick={() => {
                                  setLinesStartSurah(surah);
                                  setShowLinesSurahDropdown(false);
                                  setLinesSurahSearchText('');
                                }}
                              >
                                {surah.id}. {surah.name} - {surah.nameArabic}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Verse</label>
                    <input
                      type="number"
                      placeholder="Verse #"
                      value={linesStartVerse}
                      onChange={(e) => setLinesStartVerse(e.target.value)}
                      min="1"
                      max={linesStartSurah ? linesStartSurah.verseCount : 999}
                    />
                    {linesStartSurah && (
                      <small style={{color: '#666', display: 'block', marginTop: '3px', fontSize: '12px'}}>
                        Max: {linesStartSurah.verseCount}
                      </small>
                    )}
                  </div>
                </div>

                {/* Preview */}
                {linesStartSurah && linesStartVerse && dailyLines && (
                  <div style={{marginTop: '15px', padding: '12px', backgroundColor: '#f5f7fa', borderRadius: '8px'}}>
                    <strong style={{color: '#333'}}>Plan Summary: </strong>
                    <span style={{color: '#666'}}>
                      Starting from {linesStartSurah.name} ({linesStartVerse}), ~{dailyLines} lines per day
                    </span>
                  </div>
                )}
              </div>
            )}

            {formErrors.goal && <span className="error-text">{formErrors.goal}</span>}
          </div>

          {/* Timeline Section */}
          <div className="form-section">
            <h3>Timeline</h3>
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            {formErrors.timeline && <span className="error-text">{formErrors.timeline}</span>}
          </div>

          {/* Schedule Section */}
          <div className="form-section">
            <h3>Schedule</h3>
            <div className="form-group">
              <label>Select Days ({daysPerWeek} days per week) *</label>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px'}}>
                {daysOfWeek.map(day => {
                  const isSelected = specificDays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      className={`option-btn ${isSelected ? 'active' : ''}`}
                      onClick={() => toggleDay(day)}
                      style={{
                        flex: '1 1 calc(25% - 10px)',
                        minWidth: '120px',
                        backgroundColor: isSelected ? '#4a90e2' : '#fff',
                        color: isSelected ? '#fff' : '#333',
                        border: isSelected ? '2px solid #4a90e2' : '2px solid #ddd',
                        fontWeight: isSelected ? '600' : '400'
                      }}
                    >
                      {isSelected && '✓ '}{day}
                    </button>
                  );
                })}
              </div>
            </div>
            {formErrors.schedule && <span className="error-text">{formErrors.schedule}</span>}
          </div>

          {/* Notes Section */}
          <div className="form-section">
            <h3>Notes (Optional)</h3>
            <div className="form-group">
              <label>Add any notes about this plan</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="E.g., Focus on tajweed, complete before Ramadan, etc."
                rows="3"
                style={{width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd'}}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
          <button
            type="button"
            onClick={() => navigate('/learning-plans')}
            className="btn-secondary"
            style={{flex: 1}}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
            style={{flex: 2}}
          >
            {loading ? 'Creating Plan...' : 'Preview Assignments →'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateLearningPlanPage;
